import {Subject} from 'rxjs';

import {logger} from '../../../srcCommon/services/logger/logger';
import {ExponentialBackoffOptions} from '../../helpers/controlHelpers';
import {InboxesFiltersEnum, TeammatesMessageFoldersEnum} from '../../models/url/inboxesUrlModel';
import {AppStore} from '../../store/appStore';
import {getIsUserVisitor} from '../../store/data/teammates/teammatesSelectors';
import {PrefetcherLoopOptions, startPrefetcherLoop} from './prefetcherLoop';
import {startPrefetcherReporter} from './prefetcherReporter';
import {Prefetcher} from './prefetchers/base';
import {createConversationContentPrefetcher} from './prefetchers/conversationContent';
import {createMoreCellsPrefetcher} from './prefetchers/moreCells';
import {createPrivateTagViewsInitialCellsPrefetcher} from './prefetchers/privateTagViewsInitialCells';
import {
  createUserInboxInitialCellsPrefetcher,
  createUserMessagesInitialCellsPrefetcher,
} from './prefetchers/userInitialCells';

const prefetchLogger = logger.getLogger('prefetch');

/*
 * Constants.
 */

const prefetcherLoopOptions: PrefetcherLoopOptions = {
  // If prefetching is disabled, try again after this amount of time.
  disabledCooldownDelay: 60 * 1000,

  // If we're offline, try again after this amount of time, with jitter to avoid stampedes from clients on the same
  // network.
  offlineCooldownDelay: 30 * 1000,
  offlineCooldownJitter: 0.5,

  // If there is nothing to prefetch, check again after this amount of time.
  idleCooldownDelay: 5 * 1000,

  // If a prefetcher threw an error, try again after this amount of time, with jitter to avoid stampedes from clients
  // that encounter the same error.
  errorCooldownDelay: 5 * 60 * 1000,
  errorCooldownJitter: 0.5,
};

const cellsExponentialBackoffOptions: ExponentialBackoffOptions = {
  // Up to 2 seconds (1 second on average) between fetches.
  baseDelay: 2 * 1000,
  // Up to 1 hour (30 minutes on average with full jitter) between fetches when rate limited.
  // Worst case: 20,000 concurrent clients and a 12 req/sec rate limit.
  maxDelay: 60 * 60 * 1000,
  onComplete: (retryCount, elapsedTime) => {
    if (retryCount === 0) {
      return;
    }

    prefetchLogger.warn('Prefetcher was rate limited', {
      prefetcher: 'cells',
      retry_count: retryCount,
      duration: elapsedTime,
    });
  },
};

const conversationContentExponentialBackoffOptions: ExponentialBackoffOptions = {
  // Up to 2 seconds (1 second on average) between fetches.
  baseDelay: 2 * 1000,
  // Up to 6 minutes (3 minutes on average with full jitter) between fetches when rate limited.
  // Worst case: 20,000 concurrent clients with two P3 prefetchers and a 300 req/sec rate limit.
  maxDelay: 6 * 60 * 1000,
  onComplete: (retryCount, elapsedTime) => {
    if (retryCount === 0) {
      return;
    }

    prefetchLogger.warn('Prefetcher was rate limited', {
      prefetcher: 'timeline',
      retry_count: retryCount,
      duration: elapsedTime,
    });
  },
};

// Fetch up to 72 hours worth of more P2 pages.
const moreCellsMaxAge = 72 * 60 * 60 * 1000;

// Report the prefetcher state to Datadog at this interval.
const reportingInterval = 2 * 60 * 1000;

/*
 * API.
 */

export function setupPrefetcher(store: AppStore) {
  const initialState = store.getState();

  // Visitors don't have a P2, so there is nothing to prefetch.
  const isVisitor = getIsUserVisitor(initialState);
  if (isVisitor) {
    return;
  }

  const conversationContentPrefetcher = createConversationContentPrefetcher(
    initialState,
    conversationContentExponentialBackoffOptions,
  );
  const prefetchers: ReadonlyArray<Prefetcher<any>> = [
    // Pre-fetch the first page of the user inbox folder w/ open filter.
    createUserInboxInitialCellsPrefetcher(
      initialState,
      InboxesFiltersEnum.OPEN,
      cellsExponentialBackoffOptions,
    ),
    // Pre-fetch the first page of the user drafts folder w/ open filter.
    createUserMessagesInitialCellsPrefetcher(
      initialState,
      TeammatesMessageFoldersEnum.DRAFTS,
      cellsExponentialBackoffOptions,
    ),
    // Pre-fetch the first page of the user sent folder w/ open filter.
    createUserMessagesInitialCellsPrefetcher(
      initialState,
      TeammatesMessageFoldersEnum.SENT,
      cellsExponentialBackoffOptions,
    ),
    // Pre-fetch the first page of each pinned private tag w/ open filter.
    createPrivateTagViewsInitialCellsPrefetcher(InboxesFiltersEnum.OPEN, cellsExponentialBackoffOptions),
    // Pre-fetch the first page of the user inbox folder w/ archived and snoozed filter.
    createUserInboxInitialCellsPrefetcher(
      initialState,
      InboxesFiltersEnum.ARCHIVED,
      cellsExponentialBackoffOptions,
    ),
    createUserInboxInitialCellsPrefetcher(
      initialState,
      InboxesFiltersEnum.SNOOZED,
      cellsExponentialBackoffOptions,
    ),
    // Pre-fetch the first page of the pinned private tags w/ archived and snoozed filter.
    createPrivateTagViewsInitialCellsPrefetcher(InboxesFiltersEnum.ARCHIVED, cellsExponentialBackoffOptions),
    createPrivateTagViewsInitialCellsPrefetcher(InboxesFiltersEnum.SNOOZED, cellsExponentialBackoffOptions),
    // Pre-fetch open conversations.
    conversationContentPrefetcher,
    // Pre-fetch additional pages of P2.
    createMoreCellsPrefetcher(InboxesFiltersEnum.OPEN, moreCellsMaxAge, cellsExponentialBackoffOptions),
  ];

  const prefetcherCycles$ = new Subject<void>();

  startPrefetcherReporter(
    store.getState,
    prefetchers,
    conversationContentPrefetcher,
    prefetcherCycles$,
    reportingInterval,
    (report) => {
      prefetchLogger.info('Prefetcher report', {
        size_by_key: report.backlogLengths,
        size: report.totalBacklogLength,
        duration: report.longestTaskTime,
        min_cells_interval: Number.isFinite(report.minCellsInterval) ? report.minCellsInterval : undefined,
        has_more_cells: Number.isFinite(report.minCellsInterval),
        timeline_size: report.timelineCount,
        timeline_interval: report.timelineInterval,
        timeline_prefetched_interval: report.timelinePrefetchedInterval,
      });
    },
  );

  startPrefetcherLoop(store.dispatch, store.getState, prefetchers, {
    ...prefetcherLoopOptions,
    ticksObserver: prefetcherCycles$,
  });
}
